import React, { useEffect, useRef, useState } from 'react';
import { func, string } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { pathByRouteName } from '../../../util/routes';
import { createSaveQuery, getLicensePlateInfo, searchCarModel } from '../../../util/api';
import { SET_LICENSE_PLATE_INFO } from '../../../ducks/currentLicensePlate.duck';
import { manageDisableScrolling } from '../../../ducks/ui.duck';
import { applyCarSearch, carSearchParams } from '../../../util/carSearchParams';
import { modelCarName } from '../../../util/carVersion';
import { recordDimClick } from '../../../util/searchKeywordTracker';
import { plateInfoCarName } from '../../../util/wheelFitment';
import {
  carIdOf,
  clearRecentCars,
  forgetCar,
  formatReg,
  isCarPayload,
  isCarsOwnSearch,
  normalizeReg,
  recentCarKey,
  removeRecentCar,
  selectCar,
  selectModelCar,
  useCurrentCar,
  useRecentCars,
} from '../../../util/savedCar';
import { DIM_KEYS } from '../DimensionQuickSearch/DimensionQuickSearch';
import { getUserSavedQueries } from '../SearchPage.duck';
import { carSearchQuery } from './carSearchLocation';
import {
  axleRowsFor,
  carFactsFor,
  resolveCarTitle,
  showsSplitDiameterNote,
  sizeChipsFor,
} from './carBarData';
import CarSwitchPanel from './CarSwitchPanel';

import css from './CarBar.module.css';

/* Hjul / Däck / Fälg — the same order on a computer and a phone (BR-2). */
const PRODUCTS = [
  { key: 'wheel', label: 'Hjul' },
  { key: 'tire', label: 'Däck' },
  { key: 'rim', label: 'Fälg' },
];

const PHONE_QUERY = '(max-width: 767px)';
const isPhone = () =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(PHONE_QUERY).matches;

const CarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M5 16h14M6 16l1.5-6h9L18 16M7.5 10 9 6h6l1.5 4" />
    <circle cx="8" cy="16.5" r="1.7" />
    <circle cx="16" cy="16.5" r="1.7" />
  </svg>
);

/**
 * The car bar — the one place the search page names the buyer's car
 * (P25009-491). Full width above the filters and results on a computer; the
 * first card under the header on a phone. Same parts, same order on both.
 *
 *   Row 1  plate (or a car icon for a car chosen by model) · name ·
 *          "Sparad ✓" / "Vald via bilmodell" · Hjul / Däck / Fälg ·
 *          Bara det som passar · Visa teknisk data · Byt bil
 *   Row 2  one chip per size the car takes; a staggered car's chips are
 *          whole sets, and a chosen set offers Endast fram / Endast bak
 *   Row 3  Teknisk data, folded: the car's measurements and one row per axle
 *
 * It replaces the sidebar's "Din bil" box and car panel, the phone sheet's
 * DIN BIL box, the "Dina bilar" chip row, the product toggle in the filters
 * and the green "visar bara … som passar" bar (BR-1, BR-2).
 *
 * "Bara det som passar" is the fit-only search P25009-486 made the default of
 * a car's own search: on = only the cards the fit check passes, off =
 * passar=0. A car chosen by model gets the same, through its `carId`.
 */
const CarBar = props => {
  const { activeType, onSelectType } = props;

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();

  const { plateInfo: storePlateInfo, reg: storeReg } = useCurrentCar();
  /* Only a payload that describes a car counts — not RegNumInput's half-way
   * "choose a model / series" answers. */
  const plateCar = isCarPayload(storePlateInfo) ? storePlateInfo : null;
  /* The landing page's brand/model picker puts its car here, with no plate
   * and no car-database id: shown, but it cannot run a fit search. */
  const pickedCar = useSelector(state => (state.car?.car ? state.car : null));
  const currentUser = useSelector(state => state.user?.currentUser || null);
  const savedQueries = useSelector(state => state.SearchPage?.savedQueries) || [];
  const recents = useRecentCars();

  const data = plateCar || pickedCar;
  const reg = plateCar ? normalizeReg(storeReg) : '';
  const carId = plateCar && !reg ? carIdOf(plateCar) : null;
  /* A fit search needs to know the car: by its plate, or by its id. */
  const canFitSearch = !!plateCar && (!!reg || !!carId);
  const query = new URLSearchParams(location.search);
  const ownSearch = canFitSearch && isCarsOwnSearch(query, { data: plateCar, reg });
  const onlyFits = ownSearch && query.get('passar') !== '0';

  const [techOpen, setTechOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [regNum, setRegNum] = useState('');
  const [picking, setPicking] = useState(null);
  const [needsSeries, setNeedsSeries] = useState(null);
  const [saveState, setSaveState] = useState('idle');
  const switchRef = useRef(null);

  const hubHoleOptions =
    (config?.listing?.listingFields || []).find(f => f.key === 'hubHole')?.enumOptions || [];
  const searchPath = pathByRouteName('SearchPage', routeConfiguration);

  /* A link that names a car — a reload, a shared search — on a browser that
   * has none: look it up, so the bar can show it. Dropped when this browser's
   * own car arrives first (the cleanup cancels it). */
  const urlReg = normalizeReg(query.get('regNumber'));
  const urlCarId = query.get('carId') || '';
  useEffect(() => {
    if (data || (!urlReg && !urlCarId)) return undefined;
    let cancelled = false;
    if (urlReg) {
      getLicensePlateInfo({ licensePlate: urlReg })
        .then(result => {
          if (cancelled || !isCarPayload(result)) return;
          dispatch({ type: SET_LICENSE_PLATE_INFO, payload: result, reg: urlReg });
        })
        .catch(() => {});
    } else if (/^\d+$/.test(urlCarId)) {
      searchCarModel({ carId: urlCarId })
        .then(result => {
          if (cancelled || !isCarPayload(result)) return;
          dispatch({ type: SET_LICENSE_PLATE_INFO, payload: result, reg: null });
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [urlReg, urlCarId, !!data]);

  /* The dropdown closes on Escape and on a click outside it (computer); the
   * phone sheet closes on its backdrop and "Stäng", and holds the page still. */
  useEffect(() => {
    if (!panelOpen) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') setPanelOpen(false);
    };
    const onDown = e => {
      if (isPhone()) return;
      if (switchRef.current && !switchRef.current.contains(e.target)) setPanelOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    const lockScroll = isPhone();
    if (lockScroll) dispatch(manageDisableScrolling('CarBar.switch', true));
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      if (lockScroll) dispatch(manageDisableScrolling('CarBar.switch', false));
    };
  }, [panelOpen]);

  /* The search for a car: its fitment for the product in view, and which car
   * it is. A new car's search starts fit-only (no passar). */
  const searchForCar = (payload, plate) => {
    const next = new URLSearchParams(
      carSearchQuery({
        search: location.search,
        data: payload,
        plate,
        hubHoleOptions,
        adType: activeType,
      }).slice(1)
    );
    next.delete('passar');
    next.delete('page');
    setPanelOpen(false);
    setNeedsSeries(null);
    history.push({ pathname: searchPath, search: `?${next.toString()}` });
  };

  /* A size or a product for this car. The URL keeps naming the car, so a
   * click on the car's own sizes is the car's own (fit-checked) search. */
  const pushCarParams = params => {
    const next = new URLSearchParams(applyCarSearch(history.location.search, params).slice(1));
    if (reg) {
      next.set('regNumber', reg);
      next.delete('carId');
    } else if (carId) {
      next.set('carId', carId);
      next.delete('regNumber');
    }
    history.push({ search: `?${next.toString()}` });
  };

  /* Hjul / Däck / Fälg. With a car it is the car's search for that product —
   * bolt pattern, offset window and hub bore for Hjul and Fälg, the car alone
   * for Däck (what the plate's product picker does). Without one it is the
   * plain product filter, which clears the sizes it owns. */
  const selectProduct = type => {
    if (type === activeType && query.get('pub_adType') === type) return;
    if (onSelectType) onSelectType(type);
    if (data && (ownSearch || !canFitSearch)) {
      pushCarParams(carSearchParams({ data, adType: type, hubHoleOptions }));
      return;
    }
    const p = new URLSearchParams(history.location.search);
    DIM_KEYS.forEach(k => p.delete(k));
    p.delete('pub_combination');
    p.delete('axle');
    if (type === 'rim') p.delete('pub_tiretype');
    if (type === 'tire') p.delete('pub_rimType');
    p.set('pub_adType', type);
    p.delete('page');
    history.push({ search: `?${p.toString()}` });
  };

  const toggleFit = () => {
    const next = new URLSearchParams(location.search);
    if (onlyFits) {
      next.set('passar', '0');
      history.push({ search: `?${next.toString()}` });
    } else if (ownSearch) {
      next.delete('passar');
      history.push({ search: `?${next.toString()}` });
    } else {
      searchForCar(plateCar, reg || null);
    }
  };

  const chips = sizeChipsFor({
    data,
    adType: activeType,
    search: location.search,
    hubHoleOptions,
  });
  const activeChip = chips.find(chip => chip.active) || null;

  const pickSize = chip => {
    if (chip.active && !chip.activeAxle) {
      // The chosen size again: back to all of the car's sizes.
      pushCarParams(carSearchParams({ data, adType: activeType, hubHoleOptions }));
      return;
    }
    const tire = /(\d+)\/(\d+)\s*Z?R(\d+)/.exec(chip.key);
    recordDimClick({
      data,
      filter: {
        adType: activeType,
        ...(tire
          ? { tireWidth: tire[1], profile: tire[2], diameter: tire[3] }
          : { diameter: chip.params.pub_diameter }),
        label: chip.rear ? `${chip.front} + ${chip.rear}` : chip.front,
      },
    });
    pushCarParams(chip.params);
  };

  const pickAxle = (chip, axle) => {
    const params = chip.activeAxle === axle ? chip.params : axle === 'front' ? chip.frontParams : chip.rearParams;
    if (params) pushCarParams(params);
  };

  /* Byt bil: the car on screen, and what to pick the next one from. */
  const currentKey = plateCar ? recentCarKey({ reg, carId: carIdOf(plateCar) }) : null;
  const isSaved =
    !!reg && savedQueries.some(q => normalizeReg(q?.savedQuery?.regNumber) === reg);
  const recentByKey = new Map(recents.map(r => [recentCarKey(r), r]));
  const seen = new Set();
  const historyChips = [
    ...savedQueries
      .filter(q => normalizeReg(q?.savedQuery?.regNumber))
      .map(q => {
        const plate = normalizeReg(q.savedQuery.regNumber);
        const recent = recentByKey.get(`reg:${plate}`);
        return {
          key: `reg:${plate}`,
          reg: plate,
          series: recent?.series || q.savedQuery.series || null,
          data: recent?.data || null,
          /* The name the car has now, when this browser looked it up since
           * (with the registry's year, BR-8); else the name it was saved under. */
          label: recent?.label || q.savedQuery.title || '',
          saved: true,
          removable: false,
        };
      }),
    ...recents.map(r => ({
      key: recentCarKey(r),
      reg: r.reg || null,
      carId: r.carId || null,
      series: r.series || null,
      data: r.data || null,
      label: r.label || r.name || '',
      saved: false,
      removable: true,
    })),
  ].filter(chip => {
    if (!chip.key || seen.has(chip.key)) return false;
    seen.add(chip.key);
    return true;
  });

  const pickHistory = chip => {
    setPicking(chip.key);
    setNeedsSeries(null);
    selectCar(chip, { dispatch, userId: currentUser?.id?.uuid })
      .then(payload => {
        if (payload) {
          searchForCar(payload, chip.reg || null);
        } else if (chip.reg) {
          /* The plate needs a motor (or model) choice — that window belongs
           * to the plate field, so the plate goes there. */
          setRegNum(formatReg(chip.reg));
          setNeedsSeries(chip.reg);
        }
      })
      .catch(() => {
        if (chip.reg) {
          setRegNum(formatReg(chip.reg));
          setNeedsSeries(chip.reg);
        }
      })
      .finally(() => setPicking(null));
  };

  const searchWithoutCar = () => {
    forgetCar({ dispatch, userId: currentUser?.id?.uuid });
    setRegNum('');
    setPanelOpen(false);
    history.push({ pathname: searchPath, search: '' });
  };

  const saveCar = async () => {
    if (!plateCar || !reg || saveState === 'saving') return;
    setSaveState('saving');
    try {
      const params = {};
      const excludedKeys = ['searchType', 'page', 'stock', 'authorId', 'showByUser'];
      query.forEach((value, key) => {
        if (!excludedKeys.includes(key)) params[key] = value;
      });
      await createSaveQuery({
        savedQuery: { title: plateInfoCarName(plateCar), regNumber: reg, params },
      });
      dispatch(getUserSavedQueries());
      setSaveState('saved');
    } catch (err) {
      console.error('Failed to save car', err);
      setSaveState('idle');
    }
  };

  const carName = !data
    ? 'Välj din bil'
    : plateCar
      ? reg
        ? plateInfoCarName(plateCar) || resolveCarTitle(plateCar)
        : modelCarName(plateCar) || resolveCarTitle(plateCar)
      : resolveCarTitle(pickedCar);
  const carSub = !data ? (
    'Sök med registreringsnummer eller bilmodell'
  ) : !reg ? (
    'Vald via bilmodell'
  ) : isSaved || saveState === 'saved' ? (
    'Sparad ✓'
  ) : currentUser ? (
    <button
      type="button"
      className={css.saveCar}
      onClick={saveCar}
      disabled={saveState === 'saving'}
    >
      {saveState === 'saving' ? 'Sparar…' : 'Spara bil'}
    </button>
  ) : (
    'Via reg.nr'
  );

  const productTabs = className => (
    <div className={classNames(css.products, className)} role="tablist" aria-label="Produkt">
      {PRODUCTS.map(p => (
        <button
          key={p.key}
          type="button"
          role="tab"
          aria-selected={activeType === p.key}
          className={classNames(css.product, { [css.productActive]: activeType === p.key })}
          onClick={() => selectProduct(p.key)}
        >
          {p.label}
        </button>
      ))}
    </div>
  );

  const fitSwitch = canFitSearch ? (
    <button
      type="button"
      className={css.fitSwitch}
      onClick={toggleFit}
      aria-pressed={onlyFits}
    >
      <span className={classNames(css.track, { [css.trackOn]: onlyFits })} aria-hidden="true">
        <span className={css.knob} />
      </span>
      Bara det som passar
    </button>
  ) : null;

  const techToggle = data ? (
    <button
      type="button"
      className={css.techToggle}
      onClick={() => setTechOpen(open => !open)}
      aria-expanded={techOpen}
    >
      {techOpen ? 'Dölj teknisk data ▴' : 'Visa teknisk data ▾'}
    </button>
  ) : null;

  const facts = techOpen ? carFactsFor(data) : [];
  const axles = techOpen ? axleRowsFor({ data, chip: activeChip }) : [];

  return (
    <>
      <section className={css.root} aria-label="Din bil">
        <div className={css.rowCar}>
          <div className={css.identity}>
            {reg ? (
              <span className={css.plate} aria-label={`Registreringsnummer ${formatReg(reg)}`}>
                <span className={css.plateEu} aria-hidden="true">
                  S
                </span>
                <span className={css.plateText}>{formatReg(reg)}</span>
              </span>
            ) : (
              <span className={css.carIcon}>
                <CarIcon />
              </span>
            )}
            <span className={css.names}>
              <span className={css.carName}>{carName}</span>
              <span className={css.carSub}>{carSub}</span>
            </span>
          </div>

          {productTabs(css.productsDesktop)}

          <div className={css.rowCarActions}>
            <span className={css.desktopOnly}>{fitSwitch}</span>
            <span className={css.desktopOnly}>{techToggle}</span>
            <div className={css.switchWrap} ref={switchRef}>
              <button
                type="button"
                className={css.switchButton}
                onClick={() => setPanelOpen(open => !open)}
                aria-expanded={panelOpen}
                aria-haspopup="dialog"
              >
                <span className={css.desktopOnly}>{data ? 'Byt bil' : 'Välj bil'}</span>
                <span className={css.phoneOnly}>{data ? 'Byt' : 'Välj bil'}</span> ▾
              </button>
              <CarSwitchPanel
                isOpen={panelOpen}
                onClose={() => setPanelOpen(false)}
                chips={historyChips}
                activeKey={currentKey}
                pickingKey={picking}
                onPickChip={pickHistory}
                onRemoveChip={removeRecentCar}
                onClearHistory={clearRecentCars}
                onPlateCar={(payload, plate) => {
                  if (isCarPayload(payload) && plate) searchForCar(payload, plate);
                }}
                onModelCar={payload => {
                  selectModelCar({ data: payload }, { dispatch }).then(car => {
                    if (car) searchForCar(car, null);
                  });
                }}
                onSearchWithoutCar={searchWithoutCar}
                hasCar={!!data}
                seriesHint={needsSeries}
                regNum={regNum}
                onRegNumChange={setRegNum}
              />
            </div>
          </div>
        </div>

        {chips.length > 0 ? (
          <div className={css.rowSizes} role="group" aria-label="Dimensioner för din bil">
            {chips.map(chip => (
              <button
                key={chip.key}
                type="button"
                className={classNames(css.sizeChip, { [css.sizeChipActive]: chip.active })}
                onClick={() => pickSize(chip)}
                aria-pressed={chip.active}
              >
                <strong>{chip.front}</strong>
                {chip.rear ? (
                  <>
                    <span className={css.sizeAxle}> fram · </span>
                    <strong>{chip.rear}</strong>
                    <span className={css.sizeAxle}> bak</span>
                  </>
                ) : null}
                <span className={css.sizeTag}>{chip.tag}</span>
              </button>
            ))}
            {showsSplitDiameterNote({ data, adType: activeType }) ? (
              <span className={css.splitNote}>
                Din bil har olika fälgdiameter fram och bak — välj en dimension i taget.
              </span>
            ) : null}
            {activeChip?.isSet ? (
              <span className={css.axleChoice}>
                <span className={css.axleHint}>Byter du bara två?</span>
                {['front', 'rear'].map(axle => (
                  <button
                    key={axle}
                    type="button"
                    className={classNames(css.axleButton, {
                      [css.axleButtonActive]: activeChip.activeAxle === axle,
                    })}
                    onClick={() => pickAxle(activeChip, axle)}
                    aria-pressed={activeChip.activeAxle === axle}
                  >
                    {axle === 'front' ? 'Endast fram' : 'Endast bak'}
                  </button>
                ))}
              </span>
            ) : null}
          </div>
        ) : null}

        {fitSwitch || techToggle ? (
          <div className={classNames(css.rowControls, css.phoneOnly)}>
            {fitSwitch}
            {techToggle}
          </div>
        ) : null}

        {techOpen && (facts.length > 0 || axles.length > 0) ? (
          <div className={css.rowTech}>
            {facts.length > 0 ? (
              <dl className={css.facts}>
                {facts.map(fact => (
                  <div key={fact.key} className={css.fact}>
                    <dt>{fact.label}</dt>
                    <dd>{fact.value}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
            {axles.length > 0 ? (
              <table className={css.axles}>
                <thead>
                  <tr>
                    <th scope="col">Position</th>
                    <th scope="col">Däck</th>
                    <th scope="col">Fälg</th>
                    <th scope="col">ET</th>
                  </tr>
                </thead>
                <tbody>
                  {axles.map(row => (
                    <tr key={row.pos}>
                      <th scope="row">{row.pos}</th>
                      <td>{row.tyre}</td>
                      <td>{row.rim}</td>
                      <td>{row.et}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>
        ) : null}
      </section>

      {/* On a phone the product is chosen right under the card (BR-2). */}
      {productTabs(css.productsPhone)}
    </>
  );
};

CarBar.defaultProps = {
  activeType: 'wheel',
  onSelectType: null,
};

CarBar.propTypes = {
  activeType: string,
  onSelectType: func,
};

export default CarBar;
